import React from "react";
import { Phone, Mail, Wrench, Paintbrush } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { POSTERS } from "@/lib/business";

const ROLE_ICONS = [Wrench, Paintbrush];
const PHOTOS = ["/team/dominic-mueller.jpeg", "/team/carmine-petretta.jpeg"];
// Both portraits use the same square frame with object-cover; only object-position differs
const PHOTO_POSITIONS = ["center 18%", "center 22%"];
const PHOTO_FILTERS = [
  "brightness(1.05) contrast(1.05) saturate(1.05)",
  "brightness(1.0) contrast(1.05) saturate(1.05)",
];

export default function Team() {
  const { t } = useI18n();

  return (
    <section
      id="team"
      data-testid="team-section"
      className="relative py-16 md:py-20 bg-[#080808] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-10 max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-[#e52620]" />
            <span
              data-testid="team-eyebrow"
              className="text-[11px] uppercase tracking-[0.3em] text-zinc-400 font-bold font-mono"
            >
              {t.team.eyebrow}
            </span>
          </div>
          <h2
            data-testid="team-title"
            className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95] text-balance"
          >
            {t.team.title}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {t.team.members.map((m, i) => {
            const Icon = ROLE_ICONS[i] || Wrench;
            return (
              <article
                key={i}
                data-testid={`team-member-${i}`}
                className="group relative bg-[#0e0e0e] border border-white/10 hover:border-white/25 rounded-xl overflow-hidden transition-colors duration-500"
              >
                <div className="relative aspect-square overflow-hidden bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a]">
                  <img
                    src={PHOTOS[i]}
                    alt={m.name}
                    style={{ objectPosition: PHOTO_POSITIONS[i], filter: PHOTO_FILTERS[i] }}
                    className="w-full h-full object-cover opacity-100 group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e0e]/80 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 px-3 py-1.5 rounded-md bg-black/60 backdrop-blur-md border border-white/10 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold font-mono text-zinc-300">
                    <Icon className="w-3.5 h-3.5 text-[#e52620]" />
                    0{i + 1}
                  </div>
                </div>
                <div className="p-7 md:p-8">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#e52620] font-mono font-bold">
                    {m.role}
                  </div>
                  <h3 className="mt-2 text-2xl md:text-3xl font-bold text-white tracking-tight">
                    {m.name}
                  </h3>
                  <p className="mt-4 text-zinc-400 leading-relaxed">{m.bio}</p>
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <a
                      href={`tel:${m.phone.replace(/\s/g, "")}`}
                      data-testid={`team-member-${i}-phone`}
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-white border border-white/10 hover:border-white/30 px-4 py-2.5 rounded-md transition-colors"
                    >
                      <Phone className="w-3.5 h-3.5" />
                      {m.phone}
                    </a>
                    <a
                      href={`mailto:${m.email}`}
                      data-testid={`team-member-${i}-email`}
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-white border border-white/10 hover:border-white/30 px-4 py-2.5 rounded-md transition-colors"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      {m.email}
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
